import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  useLocation,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { startVersionPolling } from "@/lib/version-check";

import appCss from "../styles.css?url";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DevBanner } from "@/components/qorum/DevBanner";
import { SkipToContent } from "@/components/qorum/SkipToContent";
import { Toaster } from "@/components/ui/sonner";
import "@/i18n";
import { captureSentryException, initSentry, setSentryUser } from "@/lib/sentry";

import { ErrorPage } from "@/components/qorum/ErrorPage";
import { CookieBanner } from "@/components/qorum/CookieBanner";

// Init Sentry au plus tôt côté client.
if (typeof window !== "undefined") {
  initSentry();
}

function NotFoundComponent() {
  return (
    <ErrorPage
      code="404"
      eyebrow="Erreur 404 · Page introuvable"
      sub="Cette page n'existe pas dans le Volume MMXXVI."
      ctas={[
        {
          label: "← Retourner au Foyer",
          variant: "primary",
          onClick: () => {
            window.location.href = "/foyer";
          },
        },
      ]}
    />
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  void captureSentryException(error);
  const router = useRouter();

  return (
    <ErrorPage
      code="500"
      eyebrow="Erreur 500 · Le Conseil est indisponible"
      sub="Une erreur technique s'est glissée dans la délibération. L'équipe technique a été alertée."
      ctas={[
        {
          label: "Réessayer",
          variant: "outline",
          onClick: () => {
            router.invalidate();
            reset();
          },
        },
        {
          label: "Retourner au Foyer",
          variant: "primary",
          onClick: () => {
            window.location.href = "/foyer";
          },
        },
      ]}
    />
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // S13-FIX-CACHE-BUSTING : empêche le navigateur de servir un ancien
      // index.html (lequel référence d'anciens chunks JS hashés).
      { httpEquiv: "Cache-Control", content: "no-store, no-cache, must-revalidate" },
      { httpEquiv: "Pragma", content: "no-cache" },
      { httpEquiv: "Expires", content: "0" },
      { title: "Hōjin AI" },
      { name: "description", content: "Un Conseil, 7 intelligences, un verdict." },
      { name: "author", content: "Hōjin" },
      { name: "theme-color", content: "#000000" },
      { property: "og:site_name", content: "Hōjin" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@nanae_ai" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/e86a23fe-18fe-4b62-b919-500a43a21621" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/e86a23fe-18fe-4b62-b919-500a43a21621" },
      { property: "og:title", content: "Hōjin AI" },
      { name: "twitter:title", content: "Hōjin AI" },
      { property: "og:description", content: "Un Conseil, 7 intelligences, un verdict." },
      { name: "twitter:description", content: "Un Conseil, 7 intelligences, un verdict." },
      { name: "google-site-verification", content: "6fpoAXOYPfTfBtZVgEEuO8Fug3W5RInuWPOMSrxsCmc" },
    ],
    links: [
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon-64.png" },
      { rel: "icon", type: "image/png", sizes: "128x128", href: "/favicon-128.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      // Preconnect to Google Fonts hosts (early TCP/TLS handshake)
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Google Fonts CSS — fournit les @font-face avec les bonnes URLs
      // hashées (à jour). On NE preload PAS les .woff2 directement : les
      // hash Google changent sans préavis et provoquent des 404 (ex.
      // fraunces v37 / anton v25 / dmsans v16 — observé 24/06/2026).
      {
        id: "hojin-fonts",
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Anton&family=Caveat:wght@400..700&family=Cormorant+Garamond:ital,wght@0,300..700;1,300..700&family=DM+Mono:wght@300;400;500&family=DM+Sans:opsz,wght@9..40,300..700&family=Fraunces:opsz,wght,SOFT@9..144,300..900,30..100&family=JetBrains+Mono:wght@300;400;500;700&family=Oswald:wght@300..700&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap",
        // Les polices enrichissent l'identité, mais ne doivent pas bloquer le
        // premier rendu. Les fallbacks système assurent la lecture immédiate.
        media: "print",
        onLoad: (event) => {
          event.currentTarget.media = "all";
        },
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    // Analytics PLAUSIBLE (cookieless, RGPD, sans bandeau). pageview = auto ; events custom via
    // window.plausible(...). Ne reporte qu'une fois le domaine « hojin.ai » créé côté compte Plausible
    // (action Gabriel) + Publish du front. Un seul compte Plausible couvre hojin.ai + conkete.com.
    scripts: [
      { defer: true, "data-domain": "hojin.ai", src: "https://plausible.io/js/script.js" },
      { children: "window.plausible=window.plausible||function(){(window.plausible.q=window.plausible.q||[]).push(arguments)}" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const hideDevBanner =
    location.pathname.startsWith("/v/") || location.pathname === "/legal";

  // S13/S14-FIX-CACHE-BUSTING : au mount + toutes les 5 min, compare
  // /version.json avec la version persistée. Mismatch → unregister SW
  // obsolète + reload pour charger le nouveau bundle.
  useEffect(() => {
    return startVersionPolling();
  }, []);

  // Filet de sécurité pour le cas où la feuille de polices aurait fini de
  // charger avant l'hydratation et où son événement load n'aurait pas été vu.
  useEffect(() => {
    const fonts = document.getElementById("hojin-fonts") as HTMLLinkElement | null;
    if (fonts) fonts.media = "all";
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SentryUserBridge />
        <SkipToContent />
        {!hideDevBanner && <DevBanner />}
        <main id="main" tabIndex={-1}>
          <Outlet />
        </main>
        <CookieBanner />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function SentryUserBridge() {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      void setSentryUser({ id: user.id, email: user.email ?? undefined });
    } else {
      void setSentryUser(null);
    }
  }, [user]);

  return null;
}
